import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  Badge,
  Button,
} from '@etherisc/ui-kit';
import { RefreshCw, FolderIcon, BarChartIcon } from 'lucide-react';
import { useLedgerReports } from '../../hooks/ledger/useLedgerReports';

interface Account {
  id: string;
  code: string;
  name: string;
  type: string;
  normalSide: string;
  isActive: boolean;
  parentCode?: string;
  children?: Account[];
}

export function ChartOfAccounts() {
  const navigate = useNavigate();
  const { chartOfAccounts, chartOfAccountsLoading, chartOfAccountsError, refreshChartOfAccounts } = useLedgerReports();

  if (chartOfAccountsError) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-red-600">
              <p>Error loading chart of accounts: {chartOfAccountsError}</p>
              <Button onClick={refreshChartOfAccounts} className="mt-4">
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (chartOfAccountsLoading) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">Loading chart of accounts...</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Organize accounts into hierarchy
  const accounts = chartOfAccounts as Account[] || [];
  const parentAccounts = accounts.filter(a => !a.parentCode);
  const childAccounts = accounts.filter(a => a.parentCode);

  // Build hierarchy
  const accountHierarchy = parentAccounts.map(parent => ({
    ...parent,
    children: childAccounts.filter(child => child.parentCode === parent.code)
  }));

  const renderAccount = (account: Account, level: number = 0) => {
    const indent = level * 24; // 24px per level
    
    return (
      <div key={account.id}>
        <div 
          className="flex items-center justify-between p-3 border-b hover:bg-gray-50 cursor-pointer"
          style={{ paddingLeft: `${12 + indent}px` }}
          onClick={() => navigate(`/ledger/accounts/${account.code}/ledger`)}
        >
          <div className="flex items-center gap-3">
            <FolderIcon className="h-4 w-4 text-gray-400" />
            <div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm">{account.code}</span>
                <span className="font-medium">{account.name}</span>
                {!account.isActive && (
                  <Badge variant="secondary">Inactive</Badge>
                )}
              </div>
              <div className="text-sm text-gray-500">
                <Badge variant="outline">{account.type}</Badge>
                <span className="ml-2">Normal Side: {account.normalSide}</span>
              </div>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              navigate(`/ledger/reports/account-ledger/${account.code}`);
            }}
          >
            <BarChartIcon className="h-4 w-4" />
          </Button>
        </div>
        
        {/* Render children */}
        {account.children?.map(child => renderAccount(child, level + 1))}
      </div>
    );
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Chart of Accounts</h1>
          <p className="text-muted-foreground">
            Hierarchical view of all accounting accounts
          </p>
        </div>
        <Button onClick={refreshChartOfAccounts} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Accounts ({accounts.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {accountHierarchy.length > 0 ? (
            <div className="border rounded">
              {accountHierarchy.map(account => renderAccount(account))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No accounts found
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
